import { useReactTable } from "@tanstack/react-table";
import React, { useEffect } from "react";
import type { User } from "@aidkitorg/aidkit/lib/users/UserService";
import type { UserGroup } from "@aidkitorg/aidkit/lib/users/UserGroupService";
import type { Organization } from "@aidkitorg/aidkit/lib/program/admin/organization";
import { Button, Select } from "@aidkitorg/component-library";
import { SelectOptionType } from "@aidkitorg/component-library/dist/types/src/Select";
import { useLocalizedStrings } from "../../../Localization";
import { useUserContext } from "../../../Contexts/UserContext";

export type UsersFilterProps = {
  table: ReturnType<typeof useReactTable<User>>
  filters: Filters,
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  selectedOrganization: Organization | null | undefined
}

export type Filters = {
  user_group: number | undefined,
}

export const UsersFilter: React.FC<UsersFilterProps> = ({
  table,
  filters,
  setFilters,
  selectedOrganization,
}) => {
  const L = useLocalizedStrings()
  const { userGroups, loadUserGroups } = useUserContext()

  // Load user groups filtered by the selected organization from nav
  useEffect(() => {
    const orgFilter = selectedOrganization === null ? null : selectedOrganization === undefined ? undefined : selectedOrganization.uid;
    loadUserGroups(orgFilter)
  }, [selectedOrganization, loadUserGroups])

  const USER_GROUP_FILTER_VALUES: Array<SelectOptionType> = [
    {
      value: "all",
      label: L.admin_panel.user_management.all_user_groups,
    },
    ...userGroups.map(group => ({
      value: String(group.id),
      label: group.name
    }))
  ]

  const getUserGroupOption = (value: number | undefined): SelectOptionType | undefined => {
    if (value === undefined) return USER_GROUP_FILTER_VALUES.find(v => v.value === "all")
    return USER_GROUP_FILTER_VALUES.find(v => v.value === String(value))
  }

  const handleUserGroupChange = (value: SelectOptionType | null) => {
    if (!value || value.value === 'all') {
      setFilters(f => ({ ...f, user_group: undefined }))
    } else {
      setFilters(f => ({ ...f, user_group: parseInt(value.value) }))
    }
  }

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-4">
        <div>
          <Select
            className="h-10"
            options={USER_GROUP_FILTER_VALUES}
            description={L.admin_panel.user_management.select_user_group_description}
            label={L.admin_panel.user_management.user_group}
            labelLocation="above"
            value={getUserGroupOption(filters.user_group)}
            onChange={handleUserGroupChange}
          />
        </div>
      </div>
    </div>
  )
}
